/**
 * space-structure-sprites.ts — v4M4.b — per-chassis commissioned bitmap
 * sprite cache + loader for space structures.
 *
 * Architectural twin of `ship-sprites.ts` (v4M4.a) and `backdrop.ts`
 * (v4M1.8a):
 *   1. A `Map<chassis, OffscreenCanvas>` cache keyed by chassis name
 *      only (race-neutral — `drawSpaceStructure` in `ship-draw.ts`
 *      already dispatches by chassis without per-race variants).
 *   2. `loadSpaceStructureSpriteImage(chassis, url)` fetches an
 *      `HTMLImageElement`, paints into a 64×64 OffscreenCanvas, and
 *      replaces the cache entry. Headless-safe + non-rejecting per
 *      the backdrop pattern — a missing file leaves the cache empty
 *      so the consumer's procedural fallback stays active.
 *   3. `getSpaceStructureSprite(chassis)` is the cache read. Returns
 *      null when no sprite is loaded for the chassis.
 *
 * The render pipeline (`ship-draw.ts → drawSpaceStructure`) checks
 * `getSpaceStructureSprite(...)`; if non-null it blits the bitmap
 * (scaled by `STRUCTURE_SPRITE_RENDER_SCALE`) instead of running the
 * procedural per-kind silhouette. Procedural draws remain in
 * `ship-draw.ts` so the game ships fully without commissioned sprites
 * — Alpha 0.6 visual read is preserved verbatim until sprite assets
 * land in `public/assets/structures/`.
 *
 * Sprite authoring contract (per `GRAPHICS.md §4.2`):
 *   - 64×64 PNG, RGBA, transparent background.
 *   - Centred at (32, 32).
 *   - Static orientation (space structures don't rotate by heading
 *     in the renderer — the `ctx.rotate(heading + π/2)` applied for
 *     mobile ships is a no-op for stationary chassis).
 *   - Race-neutral colouring per `GRAPHICS §6.2` — the empire-accent
 *     ring + halo render separately in `system-view.ts` and are not
 *     baked into the bitmap.
 *
 * URL convention: `/assets/structures/space-<chassis-slug>.png` where
 * the slug is the chassis lowercased + non-alphanumerics collapsed to
 * single hyphens (e.g. "Warp Gate" → `space-warp-gate.png`, "Energy
 * Weapons Platform" → `space-energy-weapons-platform.png`).
 *
 * Note on overlap with v4M4.a: `tryLoadCommissionedShipSprites` in
 * `main.ts` also probes space-structure chassis under `(race, chassis)`
 * keys at `/assets/ships/<race-slug>-<chassis-slug>.png` because space
 * structures live in `data.shipTemplates`. That probe will 404
 * silently for space structures — commissioned art should land at the
 * `/assets/structures/space-*.png` path documented here. The
 * `drawSpaceStructure` branch fires before the v4M4.a ship-sprite
 * branch in `drawShip`, so the v4M4.b path takes precedence.
 */

/**
 * Canonical sprite dimensions. The 64 px tile suits structures'
 * smaller on-canvas footprint (~14 px screen extent at the
 * `STRUCTURE_SPRITE_RENDER_SCALE = 0.22` blit scale, matching the
 * procedural silhouettes in `ship-draw.ts`).
 */
const STRUCTURE_SPRITE_SIZE = 64;

/**
 * Render scale applied at blit time so the 64 px source bitmap reads
 * at roughly the procedural silhouette extent (~14 px). Tweakable in
 * v4M4.d playtest once art is in.
 */
export const STRUCTURE_SPRITE_RENDER_SCALE = 0.22;

/**
 * v4M4.d (2026-05-17) — per-chassis scale-override multiplier applied
 * on top of `STRUCTURE_SPRITE_RENDER_SCALE`. Default is 1.0 (use the
 * base scale unchanged). Entries here scale specific chassis up (or
 * down) without affecting the cohort baseline.
 *
 * Sensor Buoy at 5.0: first-playtest signal that the probe was too
 * small to read on-canvas and blended into the starfield background.
 * Bump renders the 14 px baseline at ~70 px, comfortably readable at
 * tactical zoom. Warp Gate stays at the 1.0 default — megastructure
 * silhouette already at appropriate scale relative to ships.
 *
 * Add new entries here when a chassis needs a different on-canvas
 * presence than the cohort baseline supports.
 */
export const STRUCTURE_SPRITE_SCALE_OVERRIDES: Readonly<Record<string, number>> = {
  'Sensor Buoy': 5.0,
};

/**
 * v4M4.d helper — final blit scale for the given chassis, combining
 * the cohort baseline (`STRUCTURE_SPRITE_RENDER_SCALE`) with any
 * per-chassis override from `STRUCTURE_SPRITE_SCALE_OVERRIDES`.
 *
 * Returns `STRUCTURE_SPRITE_RENDER_SCALE` unchanged when no override
 * is set for the chassis (the common case).
 */
export function getStructureSpriteScale(chassis: string): number {
  const override = STRUCTURE_SPRITE_SCALE_OVERRIDES[chassis] ?? 1.0;
  return STRUCTURE_SPRITE_RENDER_SCALE * override;
}

/** Cache key chassis → painted offscreen canvas. */
const spaceStructureSpriteCache: Map<string, OffscreenCanvas> = new Map();

/**
 * Return the cached sprite for the given chassis, or null when no
 * sprite has been loaded for it. Consumers (`ship-draw.ts`) branch
 * on this — null falls through to the procedural silhouette.
 */
export function getSpaceStructureSprite(
  chassis: string,
): OffscreenCanvas | null {
  return spaceStructureSpriteCache.get(chassis) ?? null;
}

/**
 * v4M4.b — load a commissioned space-structure sprite for the given
 * chassis. Painted into a 64×64 OffscreenCanvas regardless of source
 * dimensions; commissioned sprites should be authored at exactly
 * 64×64.
 *
 * Promise always resolves (no rejection): a failed load leaves the
 * existing cache entry untouched, the procedural fallback stays
 * active, and other in-flight loads in the batch continue.
 * Headless-safe: when `OffscreenCanvas` or `Image` is undefined the
 * load is a no-op.
 */
export function loadSpaceStructureSpriteImage(
  chassis: string,
  url: string,
): Promise<void> {
  return new Promise((resolve) => {
    if (
      typeof OffscreenCanvas === 'undefined' ||
      typeof Image === 'undefined'
    ) {
      resolve();
      return;
    }
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      try {
        const canvas = new OffscreenCanvas(
          STRUCTURE_SPRITE_SIZE,
          STRUCTURE_SPRITE_SIZE,
        );
        const ctx = canvas.getContext('2d');
        if (!ctx) {
          resolve();
          return;
        }
        ctx.clearRect(0, 0, STRUCTURE_SPRITE_SIZE, STRUCTURE_SPRITE_SIZE);
        ctx.drawImage(img, 0, 0, STRUCTURE_SPRITE_SIZE, STRUCTURE_SPRITE_SIZE);
        spaceStructureSpriteCache.set(chassis, canvas);
      } catch {
        /* leave cache untouched — procedural fallback stays active */
      }
      resolve();
    };
    img.onerror = () => resolve();
    img.src = url;
  });
}

/**
 * Test-only — clear the sprite cache. Called from unit tests so that a
 * load in one test doesn't leak into the next.
 */
export function _resetSpaceStructureSpriteCacheForTests(): void {
  spaceStructureSpriteCache.clear();
}
